const radians = degrees => degrees * Math.PI / 180
const pause = () => new Promise(resolve => setTimeout(resolve, 0))

function coordinates(position) {
  if (!position || !Number.isFinite(position.latitude) || !Number.isFinite(position.longitude)) return null
  if (Math.abs(position.latitude) > 90 || Math.abs(position.longitude) > 180) return null
  return [radians(position.latitude), radians(position.longitude)]
}

// Great-circle distance is an estimate, not road distance or travel time.
function distance(a, b) {
  const haversine = Math.sin((a[0] - b[0]) / 2) ** 2 + Math.cos(a[0]) * Math.cos(b[0]) * Math.sin((a[1] - b[1]) / 2) ** 2
  return 12742 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, haversine))))
}

async function sortSection(stops, checkpoint) {
  const size = stops.length
  if (size < 3) return stops
  const points = stops.map(stop => coordinates(stop.position))
  const distances = new Float64Array(size * size)
  for (let i = 0; i < size; i++) {
    for (let j = i + 1; j < size; j++) {
      distances[i * size + j] = distances[j * size + i] = distance(points[i], points[j])
    }
    if (i % 32 === 0) await checkpoint()
  }
  const between = (a, b) => distances[a * size + b]
  const length = order => order.slice(1).reduce((total, stop, i) => total + between(order[i], stop), 0)
  const original = stops.map((_, i) => i)
  const visited = new Uint8Array(size)
  let route = [0]
  visited[0] = 1
  while (route.length < size) {
    const last = route[route.length - 1]
    let next = -1
    for (let i = 1; i < size; i++) {
      if (!visited[i] && (next < 0 || between(last, i) < between(last, next))) next = i
    }
    visited[next] = 1
    route.push(next)
    if (route.length % 32 === 0) await checkpoint()
  }
  // Keep an already better sequence, then remove detours by reversing sections.
  // The first stop stays fixed. This is an open route, with no depot return leg.
  if (length(original) <= length(route)) route = [...original]
  for (let pass = 0; pass < 8; pass++) {
    let improved = false
    for (let i = 1; i < size - 1; i++) {
      for (let j = i + 1; j < size; j++) {
        const before = between(route[i - 1], route[i]) + (j + 1 < size ? between(route[j], route[j + 1]) : 0)
        const after = between(route[i - 1], route[j]) + (j + 1 < size ? between(route[i], route[j + 1]) : 0)
        if (after + 0.000001 < before) {
          for (let left = i, right = j; left < right; left++, right--) [route[left], route[right]] = [route[right], route[left]]
          improved = true
        }
      }
      if (i % 32 === 0) await checkpoint()
    }
    if (!improved) break
  }
  // Avoid reshuffling equivalent routes, including stops at identical coordinates.
  return length(route) + 0.000001 < length(original) ? route.map(i => stops[i]) : stops
}

export async function autoSortStops(stops, { depot, signal } = {}) {
  if (!coordinates(depot)) throw new Error("Choose a depot with map coordinates before sorting")
  const checkpoint = async () => { await pause(); signal?.throwIfAborted() }
  const result = []
  let missing = 0
  // Unknown destinations are boundaries: don't move stops across a location
  // whose distance cannot be measured. Keep its slot and the next starting stop.
  for (let index = 0; index < stops.length;) {
    if (index % 32 === 0) await checkpoint()
    if (!coordinates(stops[index].position)) {
      result.push(stops[index++])
      missing++
      continue
    }
    let end = index + 1
    while (end < stops.length && coordinates(stops[end].position)) end++
    const section = stops.slice(index, end)
    // A fixed virtual first stop lets every delivery stop move relative to the depot.
    const sorted = await sortSection(index === 0 ? [{ key: null, position: depot }, ...section] : section, checkpoint)
    result.push(...(index === 0 ? sorted.slice(1) : sorted))
    index = end
  }
  return { keys: result.map(stop => stop.key), missing }
}
